import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import hotspots from '../data/floorHotspots.json';

const IMG = {
  1: `${process.env.PUBLIC_URL || ''}/maps/floor-1.png`,
  2: `${process.env.PUBLIC_URL || ''}/maps/floor-2.png`,
};

const W = 885;
const H = 1024;

const C = {
  path: '#2f8a2a',
  pathGlow: 'rgba(255,255,255,0.92)',
  start: '#1f7a6c',
  hot: 'rgba(63, 168, 58, 0.38)',
  hotStroke: 'rgba(47, 138, 42, 0.9)',
  idle: 'rgba(255,255,255,0)',
};

const matchLabel = (label, query) => {
  if (!query || !label) return false;
  const r = String(query).trim().toLowerCase();
  const l = String(label).replace(/\n/g, ' ').trim().toLowerCase();
  if (l.includes(r) || r.includes(l)) return true;
  const num = r.replace(/[^\d]/g, '');
  if (!num) return false;
  return new RegExp(`(^|\\D)${num}(\\D|$)`).test(l);
};

function buildGraph(edges) {
  const g = {};
  edges.forEach(([a, b]) => {
    if (!g[a]) g[a] = [];
    if (!g[b]) g[b] = [];
    g[a].push(b);
    g[b].push(a);
  });
  return g;
}

function bfs(graph, from, to) {
  if (from === to) return [from];
  const q = [from];
  const prev = { [from]: null };
  while (q.length) {
    const cur = q.shift();
    for (const n of graph[cur] || []) {
      if (n in prev) continue;
      prev[n] = cur;
      if (n === to) {
        const chain = [to];
        let p = cur;
        while (p != null) {
          chain.push(p);
          p = prev[p];
        }
        return chain.reverse();
      }
      q.push(n);
    }
  }
  return null;
}

function polyLen(pts) {
  let L = 0;
  for (let i = 1; i < pts.length; i += 1) {
    L += Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1]);
  }
  return L;
}

function toD(pts) {
  return pts.map((p, i) => `${i ? 'L' : 'M'}${p[0]} ${p[1]}`).join(' ');
}

const PathOverlay = ({ points }) => {
  const ref = useRef(null);
  const [done, setDone] = useState(false);
  const d = useMemo(() => (points ? toD(points) : ''), [points]);
  const len = useMemo(() => (points ? polyLen(points) : 0), [points]);

  useLayoutEffect(() => {
    setDone(false);
    const el = ref.current;
    if (!el || !points?.length) return undefined;
    el.style.transition = 'none';
    el.style.strokeDasharray = `${len}`;
    el.style.strokeDashoffset = `${len}`;
    void el.getBoundingClientRect();
    const dur = Math.min(1.5, 0.55 + len / 450);
    el.style.transition = `stroke-dashoffset ${dur}s cubic-bezier(0.22, 1, 0.36, 1)`;
    el.style.strokeDashoffset = '0';
    const t = window.setTimeout(() => setDone(true), dur * 1000);
    return () => window.clearTimeout(t);
  }, [points, len, d]);

  if (!points?.length) return null;
  const end = points[points.length - 1];
  const prev = points[Math.max(0, points.length - 2)];
  const ang = (Math.atan2(end[1] - prev[1], end[0] - prev[0]) * 180) / Math.PI;

  return (
    <g aria-hidden="true">
      <path d={d} fill="none" stroke={C.pathGlow} strokeWidth="16" strokeLinecap="round" strokeLinejoin="round" />
      <path ref={ref} d={d} fill="none" stroke={C.path} strokeWidth="8" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={points[0][0]} cy={points[0][1]} r="12" fill={C.start} stroke="#fff" strokeWidth="3" />
      <g className={`floor-map__arrow${done ? ' is-on' : ''}`} transform={`translate(${end[0]}, ${end[1]}) rotate(${ang})`}>
        <polygon points="-18,-11 7,0 -18,11" fill={C.path} stroke="#fff" strokeWidth="2" />
      </g>
    </g>
  );
};

function doorPoint(room) {
  // точка входа в кабинет — середина стороны у коридора
  const cx = room.x + room.w / 2;
  const cy = room.y + room.h / 2;
  if (room.id?.startsWith('f1-')) {
    if (room.id.includes('stol') || room.id.includes('buh') || room.id.includes('stair2')) {
      return [room.x, cy];
    }
    if (room.id.includes('akt')) {
      return [cx, room.y + room.h];
    }
    if (
      room.door === 'd_left' ||
      room.id.includes('sport') ||
      /^f1-(7|9|10|11|12|med)$/.test(room.id)
    ) {
      return [room.x + room.w, cy];
    }
    return [room.x, cy];
  }
  // floor 2
  if (
    room.door === 'd_left' ||
    /^f2-(34|33|32|31|29|28|27)$/.test(room.id)
  ) {
    return [room.x + room.w, cy];
  }
  if (room.door === 'd_top' || room.id?.includes('akt')) {
    return [cx, room.y + room.h];
  }
  if (room.door === 'd_greek') return [cx, room.y];
  if (room.door === 'stair2' || room.door === 'd_buh') return [room.x, cy];
  if (room.id === 'f2-25') return [cx, room.y];
  return [room.x, cy];
}

function buildFloor(rooms, starts, nodesExtra, edgesExtra) {
  const nodes = { ...nodesExtra };
  const edges = [...edgesExtra];
  rooms.forEach((r) => {
    const doorId = `door_${r.id}`;
    nodes[doorId] = doorPoint(r);
    // connect shared door group to unique door
    if (r.door && nodes[r.door]) {
      edges.push([r.door, doorId]);
    } else if (r.door) {
      nodes[r.door] = nodes[doorId];
    }
    r._doorId = doorId;
  });
  return { rooms, starts, nodes, edges };
}

const F1 = buildFloor(
  hotspots.floor1,
  [{ id: 'entr1' }, { id: 'entr2' }],
  {
    // старт — «вход 1» и «вход 2»
    entr1: [95, 512],
    entr2: [430, 285],
    spine_top: [95, 200],
    spine_mid: [95, 400],
    spine_bot: [95, 750],
    open: [300, 480],
    d_akt: [95, 160],
    d_greek: [160, 200],
    d_san: [160, 300],
    d_dir: [160, 400],
    d_stair1: [160, 460],
    d_tso: [160, 610],
    d_zav: [160, 720],
    d_lib: [160, 880],
    d_sport: [130, 900],
    d_left: [130, 520],
    d_stol: [400, 250],
    d_stair2: [510, 280],
    d_buh: [510, 450],
  },
  [
    ['entr1', 'spine_mid'],
    ['entr1', 'spine_bot'],
    ['entr1', 'open'],
    ['spine_mid', 'spine_top'],
    ['open', 'entr2'],
    ['spine_top', 'd_akt'],
    ['spine_top', 'd_greek'],
    ['spine_mid', 'd_san'],
    ['spine_mid', 'd_dir'],
    ['spine_mid', 'd_stair1'],
    ['spine_mid', 'd_left'],
    ['spine_bot', 'd_left'],
    ['entr1', 'd_tso'],
    ['spine_bot', 'd_zav'],
    ['spine_bot', 'd_lib'],
    ['spine_bot', 'd_sport'],
    ['entr2', 'd_stol'],
    ['entr2', 'd_stair2'],
    ['entr2', 'd_buh'],
    ['open', 'd_buh'],
  ]
);

const F2 = buildFloor(
  hotspots.floor2,
  [{ id: 'stair1' }, { id: 'stair2' }],
  {
    // старт — лестница 1 и лестница 2
    stair1: [148, 575],
    stair2: [510, 278],
    left_top: [130, 200],
    left_mid: [130, 400],
    left_bot: [130, 750],
    top: [350, 130],
    right: [510, 400],
    d_top: [350, 114],
    d_left: [110, 500],
    d_greek: [300, 160],
    d_inner: [148, 575],
    d_buh: [510, 420],
  },
  [
    ['stair1', 'left_mid'],
    ['stair1', 'left_bot'],
    ['left_mid', 'left_top'],
    ['left_top', 'top'],
    ['top', 'stair2'],
    ['stair2', 'right'],
    ['top', 'd_top'],
    ['left_top', 'd_greek'],
    ['left_mid', 'd_left'],
    ['left_bot', 'd_left'],
    ['left_mid', 'd_inner'],
    ['left_bot', 'd_inner'],
    ['stair1', 'd_inner'],
    ['right', 'd_buh'],
    ['stair2', 'd_buh'],
  ]
);

function bestRoute(floor, roomId) {
  const room = floor.rooms.find((r) => r.id === roomId);
  if (!room) return null;
  const target = room._doorId || room.door;
  const graph = buildGraph(floor.edges);
  let best = null;
  let bestLen = Infinity;

  for (const start of floor.starts) {
    const chain = bfs(graph, start.id, target);
    if (!chain) continue;
    const pts = chain.map((id) => floor.nodes[id]).filter(Boolean);
    pts.push([room.x + room.w / 2, room.y + room.h / 2]);
    const L = polyLen(pts);
    if (L < bestLen) {
      bestLen = L;
      best = pts;
    }
  }
  return best;
}

function PhotoFloorMap({ floor, floorNum, selectedId, onSelect, hintRoom }) {
  const autoId =
    !selectedId && hintRoom
      ? floor.rooms.find((r) => matchLabel(r.label, hintRoom))?.id
      : null;
  const activeId = selectedId || autoId;

  const path = useMemo(() => {
    if (selectedId) return bestRoute(floor, selectedId);
    if (autoId) return bestRoute(floor, autoId);
    return null;
  }, [floor, selectedId, autoId]);

  return (
    <svg
      className="floor-map floor-map--photo"
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label={`План ${floorNum} этажа`}
    >
      <image href={IMG[floorNum]} xlinkHref={IMG[floorNum]} x="0" y="0" width={W} height={H} />

      {floor.rooms.map((r) => {
        const active = activeId === r.id;
        return (
          <rect
            key={r.id}
            className={`floor-map__hot${active ? ' is-hot' : ''}`}
            x={r.x}
            y={r.y}
            width={r.w}
            height={r.h}
            fill={active ? C.hot : C.idle}
            stroke={active ? C.hotStroke : 'transparent'}
            strokeWidth={active ? 2 : 0}
            vectorEffect="non-scaling-stroke"
            shapeRendering="crispEdges"
            role="button"
            tabIndex={0}
            aria-label={r.label}
            style={{ cursor: 'pointer', pointerEvents: 'all' }}
            onClick={(e) => {
              e.stopPropagation();
              onSelect?.(r.id);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSelect?.(r.id);
              }
            }}
          >
            <title>{r.label}</title>
          </rect>
        );
      })}

      <PathOverlay key={activeId || 'none'} points={path} />
    </svg>
  );
}

export const FloorOneMap = (props) => <PhotoFloorMap floor={F1} floorNum={1} {...props} />;
export const FloorTwoMap = (props) => <PhotoFloorMap floor={F2} floorNum={2} {...props} />;

export function findRoomIdByHint(floorNum, hint) {
  const floor = floorNum === 2 ? F2 : F1;
  if (!hint) return null;
  return floor.rooms.find((r) => matchLabel(r.label, hint))?.id || null;
}

export function getRoomLabel(floorNum, roomId) {
  if (!roomId) return null;
  const floor = floorNum === 2 ? F2 : F1;
  return floor.rooms.find((r) => r.id === roomId)?.label?.replace(/\n/g, ' ') || null;
}
